/**
 * Logistics data access.
 *
 * Every figure on these screens comes from the deployed API. Nothing is
 * computed in the browser and nothing from the reference workbook is baked in —
 * the staging workbook was validation data, and a number hard-coded from it
 * would be a figure that stops being true the next time somebody rides.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export interface LogisticsTotals {
  deliveries: number;
  grossAllStatuses: string;
  financiallyComplete: number;
  gross: string;
  riderPayout: string;
  companyShare: string;
  remittance: string;
  totalRevenue: string;
  anomalies: number;
  unmappedRiders: number;
}

export interface LogisticsRow {
  key: string;
  label: string;
  deliveries: number;
  grossAllStatuses: string;
  financiallyComplete: number;
  gross: string;
  riderPayout: string;
  companyShare: string;
  remittance: string | null;
  totalRevenue: string | null;
}

export interface LogisticsReport {
  range: { from: string; to: string };
  totals: LogisticsTotals;
  periods: Array<{
    id: string; label: string; startsOn: string; endsOn: string;
    accrued: string; paid: string; unpaid: string; waivedRiders: number; waived: string;
  }>;
  byGrain: LogisticsRow[];
  byDimension: LogisticsRow[];
  anomalies: Array<{ code: string; count: number; gross: string }>;
  unresolvedRiders: Array<{ riderName: string; rides: number; gross: string }>;
  /** The channel: Shop, App, WhatsApp. What decides the commission rate. */
  sources: Array<{ source: string; deliveries: number; gross: string; companyShare: string }>;
  /** How the record reached CenoX: watched happening, or read from a sheet. */
  origins: Array<{ origin: string; deliveries: number; gross: string }>;
  dimensionTruncated: boolean;
}

export interface Settlement {
  id: string;
  riderId: string;
  riderName: string;
  settlementDate: string;
  periodStart: string | null;
  periodEnd: string | null;
  settlementType: string;
  rides: number;
  riderTotalEarnings: string;
  deliveryFees: string;
  companyRevenue: string;
  riderPayable: string;
  cashCollectedByRider: string;
  cashRiderPayoutHeld: string;
  companyShareHeldByRider: string;
  cenohubOwesRider: string;
  riderOwesCenohub: string;
  finalNetPosition: string;
  remittance: string;
  riderTakeHome: string;
  branches: string[];
  deliveryIds: string[];
  status: 'unpaid' | 'paid' | 'void';
  paidAt: string | null;
  paymentMethod: string | null;
  paymentReference: string | null;
  note: string | null;
}

export interface Rider {
  id: string;
  canonicalName: string;
  phone: string | null;
  remittanceExempt: boolean;
  status: 'active' | 'inactive';
  aliases: string[];
}

export interface RateRow {
  id: string;
  rider: string | null;
  riderExternalId: string | null;
  channel: string | null;
  rateType: 'fixed' | 'percentage';
  rateValue: string;
  remittanceAmount: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
}

export interface ReportQuery {
  from: string;
  to: string;
  grain?: 'day' | 'week' | 'month' | 'year';
  dimension?: 'rider' | 'location' | 'status' | 'source' | 'origin';
  origin?: 'live' | 'import';
  /** The sidebar's geographic scope. */
  branchId?: string;
  scopeCountry?: string;
  scopeState?: string;
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * One Logistics report.
 *
 * Keyed on every input, so switching period or filter is a cache hit the second
 * time rather than a refetch — and a stale report never shows under a new
 * heading, because the key changes with the question.
 */
export function useLogisticsReport(query: ReportQuery, enabled = true): UseQueryResult<LogisticsReport> {
  const path = useBusinessPath();
  return useQuery({
    enabled,
    queryKey: ['logistics', 'report', path(''), query],
    queryFn: () => api.get<LogisticsReport>(path(`/logistics/reports${qs({ ...query })}`)),
    // Figures move as rides are recorded; a minute is fresh enough for a
    // dashboard and spares the API a request per re-render.
    staleTime: 60_000,
  });
}

export interface SettlementPage {
  data: Settlement[];
  page: number;
  pageSize: number;
  total: number;
}

export function useSettlements(filters: {
  from?: string; to?: string; riderId?: string; status?: string;
  page?: number; pageSize?: number;
}): UseQueryResult<SettlementPage> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'settlements', path(''), filters],
    queryFn: () => api.get<SettlementPage>(path(`/logistics/settlements${qs(filters)}`)),
    staleTime: 30_000,
  });
}

export function useUnsettled(range: { from?: string; to?: string } = {}): UseQueryResult<{
  data: Array<{ riderId: string | null; riderName: string; rides: number; payable: string }>;
}> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'unsettled', path(''), range],
    queryFn: () =>
      api.get<{ data: Array<{ riderId: string | null; riderName: string; rides: number; payable: string }> }>(
        path(`/logistics/settlements/unsettled${qs(range)}`),
      ),
    staleTime: 30_000,
  });
}

export function useRiders(): UseQueryResult<{ data: Rider[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'riders', path('')],
    queryFn: () => api.get<{ data: Rider[] }>(path('/logistics/riders')),
    staleTime: 5 * 60_000,
  });
}

export function useUnresolvedRiders(): UseQueryResult<{
  data: Array<{ riderName: string; rides: number; gross: string; firstSeen: string | null; lastSeen: string | null }>;
}> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'riders', 'unresolved', path('')],
    queryFn: () =>
      api.get<{
        data: Array<{ riderName: string; rides: number; gross: string; firstSeen: string | null; lastSeen: string | null }>;
      }>(path('/logistics/riders/unresolved')),
    staleTime: 5 * 60_000,
  });
}

export function useRates(): UseQueryResult<{ data: RateRow[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'rates', path('')],
    queryFn: () => api.get<{ data: RateRow[] }>(path('/logistics/rates')),
    staleTime: 5 * 60_000,
  });
}

/** Branches, for the location filter. Already an endpoint the app uses. */
export function useBranches(): UseQueryResult<{ data: Array<{ id: string; name: string }> }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['branches', path('')],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>(path('/branches')),
    staleTime: 10 * 60_000,
  });
}

export interface SettlementTotals {
  settlements: number;
  rides: number;
  riderTotalEarnings: string;
  cashCollectedByRider: string;
  cashRiderPayoutHeld: string;
  companyShareHeldByRider: string;
  cenohubOwesRider: string;
  riderOwesCenohub: string;
  remittance: string;
  finalNetPosition: string;
}

/**
 * The aggregate settlement position.
 *
 * Its own call because the list is paginated: summing a page would understate
 * what CenoHub owes the moment there were two pages, and a dashboard that
 * quietly understates money is worse than one that says nothing.
 */
export function useSettlementTotals(filters: {
  from?: string; to?: string; riderId?: string; status?: string;
} = {}): UseQueryResult<SettlementTotals> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['logistics', 'settlementTotals', path(''), filters],
    queryFn: () => api.get<SettlementTotals>(path(`/logistics/settlements/totals${qs(filters)}`)),
    staleTime: 30_000,
  });
}
